import React, { FC, useEffect, useState } from 'react';
import { ArrowsAltOutlined } from '@ant-design/icons';
import { Col, Input, message, Row, Select } from 'antd';
import { pdfjs } from 'react-pdf';
import { api, endpoints } from '../../../../api';
import DocumentPdf from '../../../../components/DocumentPdf';
import { FourLinesCollapse } from '../../../../icons';
import {
  BlueLayout,
  CustomButton,
  CustomSelect,
  ExpertiseComponentContainer,
  FormBlock,
  FormContainer,
  GridItem,
  TextLink,
} from '../../../../styles/styled';
import { ExpertiseType } from '../../../ExpertisePage/types';
import { BookType } from '../../../NewApplicationPage/types';
import { ExpertiseExpertListType } from '../../../PovsExpertisePage/types';
import { ExpertiseListFeedback, ExpertiseListFeedbacks, PovsExpertiseRightSideContainer } from '../../styled';
import {
  CollapsableItemsType,
  expertiseExpertAdaptiveScore,
  ExpertiseExpertListsType,
  expertiseExpertNoAdaptiveScore,
  expertiseExpertScore,
} from '../../types';
import { ExpertModelQues } from '../ExpertModel/ExpertModalQues';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

type PovsExpertisesToolbarProps = {
  expertise?: ExpertiseType;
  expertise_list?: ExpertiseExpertListsType;
  edit?: boolean;
  setOpenBook: (value: string | null) => void;
};

const { Option } = Select;
const { TextArea } = Input;

export const checkExpertiseExpertScore = (
  value: ExpertiseExpertListsType | ExpertiseExpertListType,
  checker: { [key: string]: string },
) =>
  Object.keys(checker)
    .map((el) => Object.keys(value).includes(el))
    .includes(false);

export const ExpertExpertisesToolbar: FC<PovsExpertisesToolbarProps> = ({ expertise, expertise_list, setOpenBook }) => {
  const [currentBook, setCurrentBook] = useState<string>('');
  const [currentSelectValue, setCurrentSelectValue] = useState<string>();
  const [expertListInputState, setExpertListInput] = useState<ExpertiseExpertListsType>({});
  const [collapsableItemsState, setCollapsableItems] = useState<CollapsableItemsType>();
  const [bookAdaptive, setBookAdaptive] = useState<boolean>();
  const [bookCartographic, setBookCartographic] = useState<boolean>();
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    try {
      if (expertise && expertise.textbook.id)
        api.get<BookType>(endpoints.getTextbookById(expertise?.textbook?.id)).then((el) => {
          setBookAdaptive(el.data.adaptive);
          setBookCartographic(el.data.cartographic_material);
        });
    } catch (e) {}
  }, [expertise]);

  function removeNullValues(obj: { [key: string]: string | number | null }) {
    return Object.keys(obj).reduce((acc, key) => {
      if (obj[key] !== null && key !== 'id' && key !== 'status' && key !== 'expertise' && key !== 'fo_reject_comment') {
        acc[key] = obj[key];
      }

      return acc;
    }, {});
  }

  useEffect(
    () => expertise_list && setExpertListInput(removeNullValues(expertise_list)),
    // eslint-disable-next-line
    [],
  );

  useEffect(() => {
    if (expertise)
      setCurrentBook(
        expertise?.textbook?.file_textbook?.file_path ||
          expertise?.textbook?.file_instruction?.file_path ||
          expertise?.textbook?.file_guideline?.file_path,
      );

    if (expertise?.textbook?.file_textbook?.file_path) setCurrentSelectValue('file_textbook');
    else if (expertise?.textbook?.file_instruction?.file_path) setCurrentSelectValue('file_instruction');
    else if (expertise?.textbook?.file_guideline?.file_path) setCurrentSelectValue('file_guideline');
  }, [expertise]);

  const confirmExpertiseList = async () =>
    api
      .put(endpoints.putExpertiseList(expertise?.expertise_list?.id || 0), expertListInputState)
      .then(() =>
        api
          .put(endpoints.sendExpertiseListToFo(expertise?.expertise_list?.id || 0))
          .then(() =>
            api
              .get(endpoints.getExpertiseListPdfById(expertise?.expertise_list?.id || 0))
              .then(() => message.success('Экспертный лист успешно отправлен ФО')),
          ),
      );

  const updateExpertiseList = () => {
    if (bookAdaptive) {
      if (
        !checkExpertiseExpertScore(expertListInputState, expertiseExpertScore) &&
        !checkExpertiseExpertScore(expertListInputState, expertiseExpertAdaptiveScore) &&
        (bookCartographic ? !checkExpertiseExpertScore(expertListInputState, { border_accuracy: '' }) : true)
      ) {
        confirmExpertiseList().then();
      } else message.error('Невозможно подтвердить экспертный лист, все критерии должны быть заполнены');
    } else {
      if (
        !checkExpertiseExpertScore(expertListInputState, expertiseExpertScore) &&
        !checkExpertiseExpertScore(expertListInputState, expertiseExpertNoAdaptiveScore) &&
        (bookCartographic ? !checkExpertiseExpertScore(expertListInputState, { border_accuracy: '' }) : true)
      )
        confirmExpertiseList().then();
      else message.error('Невозможно подтвердить экспертный лист, все критерии должны быть заполнены');
    }
  };

  return (
    <ExpertiseComponentContainer>
      <FormContainer columns padding="15px" background={'#ffffff'}>
        <div className="column-block">
          <Row gutter={[10, 10]}>
            <Col span={24}>
              <BlueLayout fontsize="16.5px" padding="0" weight="500">
                Индивидуальный лист эксперта
              </BlueLayout>
            </Col>
            <Col />
            <Col span={24}>
              <GridItem align="center" justify="flex-start" direction="row" gap="10px">
                <FourLinesCollapse />
                <TextLink onClick={() => setCollapsableItems({})}>Свернуть все ответы</TextLink>
              </GridItem>
            </Col>
          </Row>
          <br />
          <div className="scrolled">
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.1. Обеспечение соответствия содержания учебника и разработанных в комплекте с ним учебных пособий
                требованиям федерального государственного образовательного стандарта по формированию личностных,
                метапредметных и предметных результатов, содержанию примерных основных образовательных программ.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    textbook_body_accordance: !collapsableItemsState?.textbook_body_accordance,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.textbook_body_accordance && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.textbook_body_accordance}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        textbook_body_accordance: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.textbook_body_accordance_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        textbook_body_accordance_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            {!bookAdaptive && (
              <ExpertiseListFeedbacks border>
                <div className="text">
                  3.2. Принадлежность учебника и разработанных в комплекте с ним учебных пособий к совокупности
                  учебников, обеспечивающей преемственность изучения учебного предмета или предметной области на
                  соответствующем уровне образования (далее - завершенная предметная линия учебников) (за исключением
                  специального учебника).
                </div>
                {!bookAdaptive && (
                  <button
                    className="link"
                    onClick={() =>
                      setCollapsableItems({
                        ...collapsableItemsState,
                        textbook_affiliation: !collapsableItemsState?.textbook_affiliation,
                      })
                    }
                  >
                    Ответить
                  </button>
                )}

                {collapsableItemsState?.textbook_affiliation && (
                  <ExpertiseListFeedback>
                    <div className="grade">Экспертная оценка по критерию</div>
                    <CustomSelect
                      value={expertListInputState?.textbook_affiliation}
                      onChange={(value) =>
                        setExpertListInput({
                          ...expertListInputState,
                          textbook_affiliation: value as string,
                        })
                      }
                      width="100px"
                    >
                      <Option key={0} value={0}>
                        0
                      </Option>
                      <Option key={1} value={1}>
                        1
                      </Option>
                      <Option key={2} value={2}>
                        2
                      </Option>
                      <Option key={3} value={3}>
                        3
                      </Option>
                      <Option key={4} value={'-'}>
                        -
                      </Option>
                    </CustomSelect>

                    <div className="grade">Комментарий:</div>
                    <TextArea
                      value={expertListInputState?.textbook_affiliation_comment}
                      onChange={(e) =>
                        setExpertListInput({
                          ...expertListInputState,
                          textbook_affiliation_comment: e.target.value,
                        })
                      }
                    />
                  </ExpertiseListFeedback>
                )}
              </ExpertiseListFeedbacks>
            )}
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.3. Соответствие наименования учебника и разработанных в комплекте с ним учебных пособий наименованию
                учебного предмета или предметной области соответствующего федерального государственного образовательного
                стандарта.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    textbook_title_accordance: !collapsableItemsState?.textbook_title_accordance,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.textbook_title_accordance && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.textbook_title_accordance}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        textbook_title_accordance: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.textbook_title_accordance_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        textbook_title_accordance_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.4. Соответствие учебника и разработанных в комплекте с ним учебных пособий предметной концепции
                соответствующего учебного предмета или предметной области (при ее наличии).
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    concept_accordance: !collapsableItemsState?.concept_accordance,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.concept_accordance && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.concept_accordance}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        concept_accordance: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={'-'} value={'-'}>
                      -
                    </Option>
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.concept_accordance_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        concept_accordance_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.5. Полнота представления в содержании учебника и разработанных в комплекте с ним учебных пособий
                научных теорий, составляющих основу современных научных знаний по учебному предмету.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    theoretical_fullness: !collapsableItemsState?.theoretical_fullness,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.theoretical_fullness && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.theoretical_fullness}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        theoretical_fullness: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.theoretical_fullness_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        theoretical_fullness_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>

            <ExpertiseListFeedbacks border>
              <div className="text">
                3.6. Использование в содержании учебника и разработанных в комплекте с ним учебных пособий понятийного
                (терминологического) аппарата, соответствующего предметной области научного знания.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    terminology_accordance: !collapsableItemsState?.terminology_accordance,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.terminology_accordance && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.terminology_accordance}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        terminology_accordance: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.terminology_accordance_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        terminology_accordance_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>

            <ExpertiseListFeedbacks border>
              <div className="text">
                3.7. Наличие в учебнике и разработанных в комплекте с ним учебных пособиях сведений о передовых
                достижениях современной науки, техники и технологий, в том числе полученных и разработанных в рамках
                реализации Стратегии научно-технологического развития Российской Федерации, утвержденной Указом
                Президента Российской Федерации от 1 декабря 2016 г. N 642.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    scientific_news_is_present: !collapsableItemsState?.scientific_news_is_present,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.scientific_news_is_present && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.scientific_news_is_present}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        scientific_news_is_present: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.scientific_news_is_present_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        scientific_news_is_present_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>

            <ExpertiseListFeedbacks border>
              <div className="text">
                3.8. Изложение материалов учебника и разработанных в комплекте с ним учебных пособий с учетом
                особенностей и направленности образовательных программ среднего профессионального образования,
                реализуемых на базе основного общего образования или интегрированных с образовательными программами
                основного общего и среднего общего образования (для учебников, обеспечивающих реализацию образовательных
                программ среднего профессионального образования, реализуемых на базе основного общего образования или
                интегрированных с образовательными программами основного общего и среднего общего образования).
              </div>
              {collapsableItemsState?.program_accordance && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.program_accordance}
                    onChange={(defaultValue) =>
                      setExpertListInput({
                        ...expertListInputState,
                        program_accordance: defaultValue as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={'-'} value={'-'}>
                      -
                    </Option>
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.program_accordance_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        program_accordance_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    program_accordance: !collapsableItemsState?.program_accordance,
                  })
                }
              >
                Ответить
              </button>
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.9. Структурированность, логичность и последовательность содержания учебника и разработанных в
                комплекте с ним учебных пособий в изложении предметного материала.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    logic_and_structure: !collapsableItemsState?.logic_and_structure,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.logic_and_structure && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.logic_and_structure}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        logic_and_structure: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.logic_and_structure_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        logic_and_structure_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>

            <ExpertiseListFeedbacks border>
              <div className="text">
                3.10. Проработанность методического аппарата учебника и разработанных в комплекте с ним учебных пособий
                в части реализации системно-деятельностного подхода в обучении предмету, развития мотивации к учению,
                интеллектуальной, проектной и творческой деятельности обучающихся
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    methodologic_motivation: !collapsableItemsState?.methodologic_motivation,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.methodologic_motivation && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.methodologic_motivation}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        methodologic_motivation: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.methodologic_motivation_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        methodologic_motivation_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>

            <ExpertiseListFeedbacks border>
              <div className="text">
                3.11. Проработанность методического аппарата учебника и разработанных в комплекте с ним учебных пособий
                в части обеспечения овладения обучающимися приемами отбора, анализа, синтеза информации на определенную
                тему;
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    methodologic_analitic: !collapsableItemsState?.methodologic_analitic,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.methodologic_analitic && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.methodologic_analitic}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        methodologic_analitic: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.methodologic_analitic_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        methodologic_analitic_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>

            <ExpertiseListFeedbacks border>
              <div className="text">
                3.12. Проработанность методического аппарата учебника и разработанных в комплекте с ним учебных пособий
                в части формирования навыков смыслового чтения, самостоятельной учебной деятельности, применения
                полученных знаний в практической деятельности;
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    methodologic_practic: !collapsableItemsState?.methodologic_practic,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.methodologic_practic && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.methodologic_practic}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        methodologic_practic: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.methodologic_practic_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        methodologic_practic_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>

            <ExpertiseListFeedbacks border>
              <div className="text">
                3.13. Проработанность методического аппарата учебника и разработанных в комплекте с ним учебных пособий
                в части формирования критического мышления, развития аргументированного представления точки зрения.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    methodologic_critic: !collapsableItemsState?.methodologic_critic,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.methodologic_critic && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.methodologic_critic}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        methodologic_critic: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.methodologic_critic_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        methodologic_critic_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.14. Обеспечение возможности использования учебника и разработанных в комплекте с ним учебных пособий в
                групповой деятельности обучающихся, поддержке коммуникации между участниками образовательных отношений.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    group_activity: !collapsableItemsState?.group_activity,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.group_activity && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.group_activity}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        group_activity: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.group_activity_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        group_activity_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>

            <ExpertiseListFeedbacks border>
              <div className="text">
                3.15. Разнообразие видов текстовой информации, иллюстраций, материалов, представленных в графическом и
                табличном виде в содержании учебника и разработанных в комплекте с ним учебных пособий.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    text_img_diversity: !collapsableItemsState?.text_img_diversity,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.text_img_diversity && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.text_img_diversity}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        text_img_diversity: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.text_img_diversity_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        text_img_diversity_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.16. Изложение материалов учебника и разработанных в комплекте с ним учебных пособий с учетом
                возрастных особенностей психофизиологического развития обучающихся.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    age_accordance: !collapsableItemsState?.age_accordance,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.age_accordance && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.age_accordance}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        age_accordance: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.age_accordance_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        age_accordance_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            {bookAdaptive && (
              <ExpertiseListFeedbacks border>
                <div className="text">
                  3.17. Соответствие формы подачи материалов учебника и разработанных в комплекте с ним учебных пособий
                  примерным адаптированным основным образовательным программам (для специальных учебников).
                </div>
                <button
                  className="link"
                  onClick={() =>
                    setCollapsableItems({
                      ...collapsableItemsState,
                      adaptive_accordance: !collapsableItemsState?.adaptive_accordance,
                    })
                  }
                >
                  Ответить
                </button>
                {collapsableItemsState?.adaptive_accordance && (
                  <ExpertiseListFeedback>
                    <div className="grade">Экспертная оценка по критерию</div>
                    <CustomSelect
                      value={expertListInputState?.adaptive_accordance}
                      onChange={(value) =>
                        setExpertListInput({
                          ...expertListInputState,
                          adaptive_accordance: value as string,
                        })
                      }
                      width="100px"
                    >
                      <Option key={0} value={0}>
                        0
                      </Option>
                      <Option key={1} value={1}>
                        1
                      </Option>
                      <Option key={2} value={2}>
                        2
                      </Option>
                      <Option key={3} value={3}>
                        3
                      </Option>
                      <Option key={4} value={'-'}>
                        -
                      </Option>
                    </CustomSelect>

                    <div className="grade">Комментарий:</div>
                    <TextArea
                      value={expertListInputState?.adaptive_accordance_comment}
                      onChange={(e) =>
                        setExpertListInput({
                          ...expertListInputState,
                          adaptive_accordance_comment: e.target.value,
                        })
                      }
                    />
                  </ExpertiseListFeedback>
                )}
              </ExpertiseListFeedbacks>
            )}

            <ExpertiseListFeedbacks border>
              <div className="text">
                3.18. Соответствие примера рабочей программы по учебному предмету, содержащегося в методическом пособии
                для учителя, примерной основной образовательной программе и примерной рабочей программе по учебному
                предмету (при наличии).
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    example_accordance: !collapsableItemsState?.example_accordance,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.example_accordance && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.example_accordance}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        example_accordance: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.example_accordance_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        example_accordance_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.19. Отсутствие в учебнике заданий, выполнение которых обязательно непосредственно в учебнике, за
                исключением электронных форм учебника.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    no_tests_in_textbook: !collapsableItemsState?.no_tests_in_textbook,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.no_tests_in_textbook && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.no_tests_in_textbook}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        no_tests_in_textbook: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.no_tests_in_textbook_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        no_tests_in_textbook_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>

            <ExpertiseListFeedbacks border>
              <div className="text">
                3.20. Отражение в учебнике и разработанных в комплекте с ним учебных пособиях вклада российских деятелей
                науки, культуры, а также выдающихся изобретателей и инженеров России в развитие научных знаний, культуры
                и технологического развития.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    russian_science_is_present: !collapsableItemsState?.russian_science_is_present,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.russian_science_is_present && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.russian_science_is_present}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        russian_science_is_present: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.russian_science_is_present_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        russian_science_is_present_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.21. Наличие в тексте учебника и разработанных в комплекте с ним учебных пособий ссылок на
                дополнительные источники информации по предмету, ориентирующих обучающихся использовать достоверные и
                актуальные источники информации.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    links_in_texetbook_is_present: !collapsableItemsState?.links_in_texetbook_is_present,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.links_in_texetbook_is_present && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.links_in_texetbook_is_present}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        links_in_texetbook_is_present: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.links_in_texetbook_is_present_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        links_in_texetbook_is_present_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.22. Направленность содержания учебника и разработанных в комплекте с ним учебных пособий на
                формирование у обучающихся российской гражданской идентичности, ценностных ориентиров верховенства
                права, поддержания общественной безопасности, свободы и ответственности.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    for_all_good: !collapsableItemsState?.for_all_good,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.for_all_good && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.for_all_good}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        for_all_good: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.for_all_good_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        for_all_good_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            {bookCartographic && (
              <ExpertiseListFeedbacks border>
                <div className="text">
                  3.23. Правильность отображения Государственной границы Российской Федерации и территории Российской
                  Федерации, территорий субъектов Российской Федерации и территорий муниципальных образований, а также
                  наименований географических объектов Российской Федерации в соответствии с требованиями
                  законодательства в области наименований географических объектов, а также политического устройства
                  мира: названий государств и территорий, их столиц и центров, достоверности отображения государственных
                  границ иностранных государств на картографическом материале, представленном в учебнике (при проведении
                  экспертизы учебников и разработанных в комплекте с ними учебных пособий, содержащих картографический
                  материал).
                </div>
                <button
                  className="link"
                  onClick={() =>
                    setCollapsableItems({
                      ...collapsableItemsState,
                      border_accuracy: !collapsableItemsState?.border_accuracy,
                    })
                  }
                >
                  Ответить
                </button>
                {collapsableItemsState?.border_accuracy && (
                  <ExpertiseListFeedback>
                    <div className="grade">Экспертная оценка по критерию</div>
                    <CustomSelect
                      value={expertListInputState?.border_accuracy}
                      onChange={(value) =>
                        setExpertListInput({
                          ...expertListInputState,
                          border_accuracy: value as string,
                        })
                      }
                      width="100px"
                    >
                      <Option key={4} value={'-'}>
                        -
                      </Option>
                      <Option key={0} value={0}>
                        0
                      </Option>
                      <Option key={1} value={1}>
                        1
                      </Option>
                      <Option key={2} value={2}>
                        2
                      </Option>
                      <Option key={3} value={3}>
                        3
                      </Option>
                    </CustomSelect>

                    <div className="grade">Комментарий:</div>
                    <TextArea
                      value={expertListInputState?.border_accuracy_comment}
                      onChange={(e) =>
                        setExpertListInput({
                          ...expertListInputState,
                          border_accuracy_comment: e.target.value,
                        })
                      }
                    />
                  </ExpertiseListFeedback>
                )}
              </ExpertiseListFeedbacks>
            )}
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.24. Отсутствие в учебнике и разработанных в комплекте с ним учебных пособиях ошибок, в том числе
                грамматических, орфографических, пунктуационных, речевых, опечаток (в том числе применительно к
                иллюстративному материалу, комментариям, примечаниям).
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    mistakes_are_missing: !collapsableItemsState?.mistakes_are_missing,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.mistakes_are_missing && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.mistakes_are_missing}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        mistakes_are_missing: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.mistakes_are_missing_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        mistakes_are_missing_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.25. Соответствие текста учебника и разработанных в комплекте с ним учебных пособий нормам речи и
                правилам речевого этикета языка, на котором изданы учебник и разработанные в комплекте с ним учебные
                пособия, а также отсутствие в учебнике и разработанных в комплекте с ним учебных пособиях ненормативной
                лексики.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    textbook_lang_accordance: !collapsableItemsState?.textbook_lang_accordance,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.textbook_lang_accordance && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.textbook_lang_accordance}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        textbook_lang_accordance: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.textbook_lang_accordance_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        textbook_lang_accordance_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.26. Соответствие иллюстративного материала, содержащегося в учебнике и разработанных в комплекте с ним
                учебных пособиях, целям достижения образовательных результатов по образовательным программам
                соответствующего уровня общего образования и направленности.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    textbook_img_accordance: !collapsableItemsState?.textbook_img_accordance,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.textbook_img_accordance && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.textbook_img_accordance}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        textbook_img_accordance: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.textbook_img_accordance_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        textbook_img_accordance_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.27. Отсутствие в учебнике и разработанных в комплекте с ним учебных пособиях сведений (информации),
                противоречащих (противоречащей) Конституции Российской Федерации и законодательству Российской Федерации
                и (или) оправдывающих (оправдывающей) противоправное поведение.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    criminal_is_missing: !collapsableItemsState?.criminal_is_missing,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.criminal_is_missing && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.criminal_is_missing}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        criminal_is_missing: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.criminal_is_missing_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        criminal_is_missing_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.28. Отсутствие в учебнике и разработанных в комплекте с ним учебных пособиях сведений (информации),
                направленных (направленной) на формирование неуважительного отношения к Российской Федерации, органам
                государственной власти и (или) органам местного самоуправления, народам, проживающим в Российской
                Федерации, конфессиям, социальным группам.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    impolite_is_missing: !collapsableItemsState?.impolite_is_missing,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.impolite_is_missing && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.impolite_is_missing}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        impolite_is_missing: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.impolite_is_missing_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        impolite_is_missing_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.29. Отсутствие в учебнике и разработанных в комплекте с ним учебных пособиях сведений (информации),
                побуждающих (побуждающей) детей к совершению действий, представляющих угрозу их жизни и (или) здоровью,
                в том числе к причинению вреда своему здоровью и жизни (самоубийству).
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    suicide_is_missing: !collapsableItemsState?.suicide_is_missing,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.suicide_is_missing && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.suicide_is_missing}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        suicide_is_missing: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.suicide_is_missing_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        suicide_is_missing_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.30. Отсутствие в учебнике и разработанных в комплекте с ним учебных пособиях сведений (информации),
                побуждающих (побуждающей) к употреблению наркотических, психотропных и (или) одурманивающих веществ,
                табака, алкогольной и спиртосодержащей продукции, а также безалкогольных тонизирующих напитков, в том
                числе энергетических.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    addiction_is_missing: !collapsableItemsState?.addiction_is_missing,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.addiction_is_missing && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.addiction_is_missing}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        addiction_is_missing: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.addiction_is_missing_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        addiction_is_missing_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.31. Отсутствие в учебнике и разработанных в комплекте с ним учебных пособиях сведений (информации),
                побуждающих (побуждающей) к асоциальному поведению и (или) образу жизни, в том числе азартным играм,
                попрошайничеству, бродяжничеству, проституции.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    asocial_is_missing: !collapsableItemsState?.asocial_is_missing,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.asocial_is_missing && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.asocial_is_missing}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        asocial_is_missing: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.asocial_is_missing_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        asocial_is_missing_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>

            <ExpertiseListFeedbacks border>
              <div className="text">
                3.32. Отсутствие в учебнике и разработанных в комплекте с ним учебных пособиях сведений (информации),
                обосновывающих (обосновывающей) или оправдывающих (оправдывающей) допустимость насилия и (или)
                жестокости либо побуждающих (побуждающей) осуществить насильственные действия по отношению к людям или
                животным.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    violence_is_missing: !collapsableItemsState?.violence_is_missing,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.violence_is_missing && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.violence_is_missing}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        violence_is_missing: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.violence_is_missing_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        violence_is_missing_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.33. Отсутствие в учебнике и разработанных в комплекте с ним учебных пособиях сведений (информации),
                направленных (направленной) на поддержку и (или) оправдание экстремизма и терроризма, в том числе ссылок
                на информационные материалы, содержащиеся в федеральном списке экстремистских материалов и (или) на
                доменные имена, указатели страниц сайтов в информационно-телекоммуникационной сети {'"Интернет"'} и
                сетевые адреса, позволяющие идентифицировать сайты в информационно-телекоммуникационной сети{' '}
                {'"Интернет"'}, содержащиеся в единой автоматизированной информационной системе {'"Единый'} реестр
                доменных имен, указателей страниц сайтов в сети {'"Интернет"'} и сетевых адресов, позволяющих
                идентифицировать сайты в сети
                {'"Интернет"'}, содержащие информацию, распространение которой в Российской Федерации {'запрещено"'}.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    terrorism_is_missing: !collapsableItemsState?.terrorism_is_missing,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.terrorism_is_missing && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.terrorism_is_missing}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        terrorism_is_missing: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.terrorism_is_missing_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        terrorism_is_missing_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.34. Отсутствие в учебнике и разработанных в комплекте с ним учебных пособиях информации, отрицающей
                семейные ценности, пропагандирующей нетрадиционные сексуальные отношения и формирующей неуважение к
                родителям и (или) другим членам семьи.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    lgbt_is_missing: !collapsableItemsState?.lgbt_is_missing,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.lgbt_is_missing && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.lgbt_is_missing}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        lgbt_is_missing: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.lgbt_is_missing_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        lgbt_is_missing_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                3.35. Отсутствие в содержании (в том числе в иллюстративном материале) учебника и разработанных в
                комплекте с ним учебных пособий недостоверных, научно неподтвержденных и сфабрикованных фактов, в том
                числе о событиях и участниках Второй мировой войны.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    fake_is_missing: !collapsableItemsState?.fake_is_missing,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.fake_is_missing && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.fake_is_missing}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        fake_is_missing: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.fake_is_missing_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        fake_is_missing_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>

            <ExpertiseListFeedbacks border>
              <div className="text">
                4.1. Соответствие структуры и содержания цифровых копий и электронных форм учебника и разработанных в
                комплекте с ним учебных пособий друг другу.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    web_and_paper_accordance: !collapsableItemsState?.web_and_paper_accordance,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.web_and_paper_accordance && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.web_and_paper_accordance}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        web_and_paper_accordance: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.web_and_paper_accordance_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        web_and_paper_accordance_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                4.2. Корректное отображение и функционирование электронных форм учебника и разработанных в комплекте с
                ним учебных пособий с помощью программ для трех или более операционных систем, не менее двух из которых
                - для мобильных устройств.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    correct_web_os: !collapsableItemsState?.correct_web_os,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.correct_web_os && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.correct_web_os}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        correct_web_os: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.correct_web_oscomment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        correct_web_oscomment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                4.3. Корректное отображение и функционирование электронных форм учебника и разработанных в комплекте с
                ним учебных пособий не менее чем на двух видах электронных устройств (стационарном или персональном
                компьютере, в том числе с подключением к интерактивной доске, планшетном компьютере, и иным электронным
                устройствам вывода информации).
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    correct_web_device: !collapsableItemsState?.correct_web_device,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.correct_web_device && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.correct_web_device}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        correct_web_device: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.correct_web_device_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        correct_web_device_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                4.4. Наличие возможности создания пользователем в электронной форме учебника и разработанных в комплекте
                с ним учебных пособий заметок, закладок и перехода к ним.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    web_aliacies_is_present: !collapsableItemsState?.web_aliacies_is_present,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.web_aliacies_is_present && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.web_aliacies_is_present}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        web_aliacies_is_present: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.web_aliacies_is_present_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        web_aliacies_is_present_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                4.5. Наличие возможности определения номера страниц цифровой копии учебника и разработанных в комплекте
                с ним учебных пособий, на которой расположено содержание текущей страницы учебника и разработанных в
                комплекте с ним учебных пособий в электронной форме.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    web_page_numbers: !collapsableItemsState?.web_page_numbers,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.web_page_numbers && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.web_page_numbers}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        web_page_numbers: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.web_page_numbers_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        web_page_numbers_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            <ExpertiseListFeedbacks border>
              <div className="text">
                4.6. Наличие в электронной форме учебника и разработанных в комплекте с ним учебных пособий дидактически
                обоснованных и методически проработанных мультимедийных и интерактивных средств, активизирующих
                познавательные способности и повышающих эффективность обучения по предмету.
              </div>
              <button
                className="link"
                onClick={() =>
                  setCollapsableItems({
                    ...collapsableItemsState,
                    web_interactive_is_present: !collapsableItemsState?.web_interactive_is_present,
                  })
                }
              >
                Ответить
              </button>
              {collapsableItemsState?.web_interactive_is_present && (
                <ExpertiseListFeedback>
                  <div className="grade">Экспертная оценка по критерию</div>
                  <CustomSelect
                    value={expertListInputState?.web_interactive_is_present}
                    onChange={(value) =>
                      setExpertListInput({
                        ...expertListInputState,
                        web_interactive_is_present: value as string,
                      })
                    }
                    width="100px"
                  >
                    <Option key={0} value={0}>
                      0
                    </Option>
                    <Option key={1} value={1}>
                      1
                    </Option>
                    <Option key={2} value={2}>
                      2
                    </Option>
                    <Option key={3} value={3}>
                      3
                    </Option>
                  </CustomSelect>

                  <div className="grade">Комментарий:</div>
                  <TextArea
                    value={expertListInputState?.web_interactive_is_present_comment}
                    onChange={(e) =>
                      setExpertListInput({
                        ...expertListInputState,
                        web_interactive_is_present_comment: e.target.value,
                      })
                    }
                  />
                </ExpertiseListFeedback>
              )}
            </ExpertiseListFeedbacks>
            {bookAdaptive && (
              <ExpertiseListFeedbacks border>
                <div className="text">
                  5.1. Печатная форма учебника и разработанных в комплекте с ним учебных пособий должна быть выполнена
                  шрифтом Брайля.
                </div>
                {bookAdaptive ? (
                  <button
                    className="link"
                    onClick={() =>
                      setCollapsableItems({
                        ...collapsableItemsState,
                        brile_is_present: !collapsableItemsState?.brile_is_present,
                      })
                    }
                  >
                    Ответить
                  </button>
                ) : (
                  <p className="commentCheck">
                    Для учебников, реализующих адаптированную общеобразовательную программу
                  </p>
                )}
                {collapsableItemsState?.brile_is_present && (
                  <ExpertiseListFeedback>
                    <div className="grade">Экспертная оценка по критерию</div>
                    <CustomSelect
                      value={expertListInputState?.brile_is_present}
                      onChange={(defaultValue) =>
                        setExpertListInput({
                          ...expertListInputState,
                          brile_is_present: defaultValue as string,
                        })
                      }
                      width="100px"
                    >
                      <Option key={4} value={'-'}>
                        -
                      </Option>
                      <Option key={0} value={0}>
                        0
                      </Option>
                      <Option key={1} value={1}>
                        1
                      </Option>
                      <Option key={2} value={2}>
                        2
                      </Option>
                      <Option key={3} value={3}>
                        3
                      </Option>
                    </CustomSelect>

                    <div className="grade">Комментарий:</div>
                    <TextArea
                      value={expertListInputState?.brile_is_present_comment}
                      onChange={(e) =>
                        setExpertListInput({
                          ...expertListInputState,
                          brile_is_present_comment: e.target.value,
                        })
                      }
                    />
                  </ExpertiseListFeedback>
                )}
              </ExpertiseListFeedbacks>
            )}
            {bookAdaptive && (
              <ExpertiseListFeedbacks border>
                <div className="text">
                  5.2. Наличие схем, таблиц, иллюстраций, выполненных в рельефно-графическом формате, которые уточняют и
                  конкретизируют содержание учебника и разработанных в комплекте с ним учебных пособий и способствуют
                  компенсации недостатка чувственного опыта у слепых и слабовидящих обучающихся.
                </div>
                {bookAdaptive ? (
                  <button
                    className="link"
                    onClick={() =>
                      setCollapsableItems({
                        ...collapsableItemsState,
                        relief_is_present: !collapsableItemsState?.relief_is_present,
                      })
                    }
                  >
                    Ответить
                  </button>
                ) : (
                  <p className="commentCheck">
                    Для учебников, реализующих адаптированную общеобразовательную программу
                  </p>
                )}
                {collapsableItemsState?.relief_is_present && (
                  <ExpertiseListFeedback>
                    <div className="grade">Экспертная оценка по критерию</div>
                    <CustomSelect
                      value={expertListInputState?.relief_is_present}
                      onChange={(value) =>
                        setExpertListInput({
                          ...expertListInputState,
                          relief_is_present: value as string,
                        })
                      }
                      width="100px"
                    >
                      <Option key={4} value={'-'}>
                        -
                      </Option>
                      <Option key={0} value={0}>
                        0
                      </Option>
                      <Option key={1} value={1}>
                        1
                      </Option>
                      <Option key={2} value={2}>
                        2
                      </Option>
                      <Option key={3} value={3}>
                        3
                      </Option>
                    </CustomSelect>

                    <div className="grade">Комментарий:</div>
                    <TextArea
                      value={expertListInputState?.relief_is_present_comment}
                      onChange={(e) =>
                        setExpertListInput({
                          ...expertListInputState,
                          relief_is_present_comment: e.target.value,
                        })
                      }
                    />
                  </ExpertiseListFeedback>
                )}
              </ExpertiseListFeedbacks>
            )}
            {bookAdaptive && (
              <ExpertiseListFeedbacks border>
                <div className="text">
                  5.3. Наличие (за исключением учебников, предназначенных для реализации адаптированных основных
                  общеобразовательных программ начального общего образования, а также в случае, когда большая часть
                  информации в учебнике представлена в графической форме (учебники по предмету «Изобразительное
                  искусство», «Технология», «Физическая культура»)) электронного приложения к учебнику, которое
                  расценивается не является как аналогом электронной формы учебника для данной категории обучающихся.
                </div>
                {bookAdaptive ? (
                  <button
                    className="link"
                    onClick={() =>
                      setCollapsableItems({
                        ...collapsableItemsState,
                        application_is_present: !collapsableItemsState?.application_is_present,
                      })
                    }
                  >
                    Ответить
                  </button>
                ) : (
                  <p className="commentCheck">
                    Для учебников, реализующих адаптированную общеобразовательную программу
                  </p>
                )}
                {collapsableItemsState?.application_is_present && (
                  <ExpertiseListFeedback>
                    <div className="grade">Экспертная оценка по критерию</div>
                    <CustomSelect
                      value={expertListInputState?.application_is_present}
                      onChange={(value) =>
                        setExpertListInput({
                          ...expertListInputState,
                          application_is_present: value as string,
                        })
                      }
                      width="100px"
                    >
                      <Option key={4} value={'-'}>
                        -
                      </Option>
                      <Option key={0} value={0}>
                        0
                      </Option>
                      <Option key={1} value={1}>
                        1
                      </Option>
                      <Option key={2} value={2}>
                        2
                      </Option>
                      <Option key={3} value={3}>
                        3
                      </Option>
                    </CustomSelect>

                    <div className="grade">Комментарий:</div>
                    <TextArea
                      value={expertListInputState?.application_is_present_comment}
                      onChange={(e) =>
                        setExpertListInput({
                          ...expertListInputState,
                          application_is_present_comment: e.target.value,
                        })
                      }
                    />
                  </ExpertiseListFeedback>
                )}
              </ExpertiseListFeedbacks>
            )}
            {bookAdaptive && (
              <ExpertiseListFeedbacks border>
                <div className="text">
                  6.1. Соответствие электронного приложения содержанию и структуре печатной формы учебника и
                  разработанным в комплекте с ним учебным пособиям (за исключением графических объектов и связанных с
                  ними текстовых пояснений, заданий и упражнений).
                </div>
                {bookAdaptive ? (
                  <button
                    className="link"
                    onClick={() =>
                      setCollapsableItems({
                        ...collapsableItemsState,
                        application_web_and_paper_accordance:
                          !collapsableItemsState?.application_web_and_paper_accordance,
                      })
                    }
                  >
                    Ответить
                  </button>
                ) : (
                  <p className="commentCheck">
                    Для учебников, реализующих адаптированную общеобразовательную программу
                  </p>
                )}
                {collapsableItemsState?.application_web_and_paper_accordance && (
                  <ExpertiseListFeedback>
                    <div className="grade">Экспертная оценка по критерию</div>
                    <CustomSelect
                      value={expertListInputState?.application_web_and_paper_accordance}
                      onChange={(value) =>
                        setExpertListInput({
                          ...expertListInputState,
                          application_web_and_paper_accordance: value as string,
                        })
                      }
                      width="100px"
                    >
                      <Option key={0} value={0}>
                        0
                      </Option>
                      <Option key={1} value={1}>
                        1
                      </Option>
                      <Option key={2} value={2}>
                        2
                      </Option>
                      <Option key={3} value={3}>
                        3
                      </Option>
                      <Option key={4} value={'-'}>
                        -
                      </Option>
                    </CustomSelect>

                    <div className="grade">Комментарий:</div>
                    <TextArea
                      value={expertListInputState?.application_web_and_paper_accordance_comment}
                      onChange={(e) =>
                        setExpertListInput({
                          ...expertListInputState,
                          application_web_and_paper_accordance_comment: e.target.value,
                        })
                      }
                    />
                  </ExpertiseListFeedback>
                )}
              </ExpertiseListFeedbacks>
            )}
            {bookAdaptive && (
              <ExpertiseListFeedbacks border>
                <div className="text">
                  6.2. Представление электронного приложения в плоскопечатном текстовом или звуковом формате.
                </div>
                {bookAdaptive ? (
                  <button
                    className="link"
                    onClick={() =>
                      setCollapsableItems({
                        ...collapsableItemsState,
                        application_flat_printed: !collapsableItemsState?.application_flat_printed,
                      })
                    }
                  >
                    Ответить
                  </button>
                ) : (
                  <p className="commentCheck">
                    Для учебников, реализующих адаптированную общеобразовательную программу
                  </p>
                )}
                {collapsableItemsState?.application_flat_printed && (
                  <ExpertiseListFeedback>
                    <div className="grade">Экспертная оценка по критерию</div>
                    <CustomSelect
                      value={expertListInputState?.application_flat_printed}
                      onChange={(value) =>
                        setExpertListInput({
                          ...expertListInputState,
                          application_flat_printed: value as string,
                        })
                      }
                      width="100px"
                    >
                      <Option key={0} value={0}>
                        0
                      </Option>
                      <Option key={1} value={1}>
                        1
                      </Option>
                      <Option key={2} value={2}>
                        2
                      </Option>
                      <Option key={3} value={3}>
                        3
                      </Option>
                      <Option key={4} value={'-'}>
                        -
                      </Option>
                    </CustomSelect>

                    <div className="grade">Комментарий:</div>
                    <TextArea
                      value={expertListInputState?.application_flat_printed_comment}
                      onChange={(e) =>
                        setExpertListInput({
                          ...expertListInputState,
                          application_flat_printed_comment: e.target.value,
                        })
                      }
                    />
                  </ExpertiseListFeedback>
                )}
              </ExpertiseListFeedbacks>
            )}
            {bookAdaptive && (
              <ExpertiseListFeedbacks border>
                <div className="text">
                  6.3. Наличие возможности воспроизведения электронного приложения при помощи тифлофлешплеера,
                  персонального или планшетного компьютера под управлением программы экранного доступа, а также чтения
                  электронного приложения с помощью дисплеев Брайля.
                </div>
                {bookAdaptive ? (
                  <button
                    className="link"
                    onClick={() =>
                      setCollapsableItems({
                        ...collapsableItemsState,
                        application_tiflopleer: !collapsableItemsState?.application_tiflopleer,
                      })
                    }
                  >
                    Ответить
                  </button>
                ) : (
                  <p className="commentCheck">
                    Для учебников, реализующих адаптированную общеобразовательную программу
                  </p>
                )}
                {collapsableItemsState?.application_tiflopleer && (
                  <ExpertiseListFeedback>
                    <div className="grade">Экспертная оценка по критерию</div>
                    <CustomSelect
                      value={expertListInputState?.application_tiflopleer}
                      onChange={(value) =>
                        setExpertListInput({
                          ...expertListInputState,
                          application_tiflopleer: value as string,
                        })
                      }
                      width="100px"
                    >
                      <Option key={0} value={0}>
                        0
                      </Option>
                      <Option key={1} value={1}>
                        1
                      </Option>
                      <Option key={2} value={2}>
                        2
                      </Option>
                      <Option key={3} value={3}>
                        3
                      </Option>
                      <Option key={4} value={'-'}>
                        -
                      </Option>
                    </CustomSelect>

                    <div className="grade">Комментарий:</div>
                    <TextArea
                      value={expertListInputState?.application_tiflopleer_comment}
                      onChange={(e) =>
                        setExpertListInput({
                          ...expertListInputState,
                          application_tiflopleer_comment: e.target.value,
                        })
                      }
                    />
                  </ExpertiseListFeedback>
                )}
              </ExpertiseListFeedbacks>
            )}
            {bookAdaptive && (
              <ExpertiseListFeedbacks border>
                <div className="text">
                  6.4. Наличие в электронном приложении элементов управления, обеспечивающих быструю навигацию по
                  документу (разметка по заголовкам, перекрёстные ссылки).
                </div>
                {bookAdaptive ? (
                  <button
                    className="link"
                    onClick={() =>
                      setCollapsableItems({
                        ...collapsableItemsState,
                        application_navigation_is_present: !collapsableItemsState?.application_navigation_is_present,
                      })
                    }
                  >
                    Ответить
                  </button>
                ) : (
                  <p className="commentCheck">
                    Для учебников, реализующих адаптированную общеобразовательную программу
                  </p>
                )}
                {collapsableItemsState?.application_navigation_is_present && (
                  <ExpertiseListFeedback>
                    <div className="grade">Экспертная оценка по критерию</div>
                    <CustomSelect
                      value={expertListInputState?.application_navigation_is_present}
                      onChange={(value) =>
                        setExpertListInput({
                          ...expertListInputState,
                          application_navigation_is_present: value as string,
                        })
                      }
                      width="100px"
                    >
                      <Option key={0} value={0}>
                        0
                      </Option>
                      <Option key={1} value={1}>
                        1
                      </Option>
                      <Option key={2} value={2}>
                        2
                      </Option>
                      <Option key={3} value={3}>
                        3
                      </Option>
                      <Option key={4} value={'-'}>
                        -
                      </Option>
                    </CustomSelect>

                    <div className="grade">Комментарий:</div>
                    <TextArea
                      value={expertListInputState?.application_navigation_is_present_comment}
                      onChange={(e) =>
                        setExpertListInput({
                          ...expertListInputState,
                          application_navigation_is_present_comment: e.target.value,
                        })
                      }
                    />
                  </ExpertiseListFeedback>
                )}
              </ExpertiseListFeedbacks>
            )}
            {bookAdaptive && (
              <ExpertiseListFeedbacks border>
                <div className="text">
                  6.5. Замена простых графических объектов (схем, иллюстраций), содержащихся в печатной форме учебника и
                  разработанных в комплекте с ним учебных пособиях, текстовыми пояснениями или тифлокомментариями.
                </div>
                {bookAdaptive ? (
                  <button
                    className="link"
                    onClick={() =>
                      setCollapsableItems({
                        ...collapsableItemsState,
                        img_to_tiflo_is_present: !collapsableItemsState?.img_to_tiflo_is_present,
                      })
                    }
                  >
                    Ответить
                  </button>
                ) : (
                  <p className="commentCheck">
                    Для учебников, реализующих адаптированную общеобразовательную программу
                  </p>
                )}
                {collapsableItemsState?.img_to_tiflo_is_present && (
                  <ExpertiseListFeedback>
                    <div className="grade">Экспертная оценка по критерию</div>
                    <CustomSelect
                      value={expertListInputState?.img_to_tiflo_is_present}
                      onChange={(value) =>
                        setExpertListInput({
                          ...expertListInputState,
                          img_to_tiflo_is_present: value as string,
                        })
                      }
                      width="100px"
                    >
                      <Option key={0} value={0}>
                        0
                      </Option>
                      <Option key={1} value={1}>
                        1
                      </Option>
                      <Option key={2} value={2}>
                        2
                      </Option>
                      <Option key={3} value={3}>
                        3
                      </Option>
                      <Option key={4} value={'-'}>
                        -
                      </Option>
                    </CustomSelect>

                    <div className="grade">Комментарий:</div>
                    <TextArea
                      value={expertListInputState?.img_to_tiflo_is_present_comment}
                      onChange={(e) =>
                        setExpertListInput({
                          ...expertListInputState,
                          img_to_tiflo_is_present_comment: e.target.value,
                        })
                      }
                    />
                  </ExpertiseListFeedback>
                )}
              </ExpertiseListFeedbacks>
            )}
          </div>
        </div>

        <div className="column-block">
          <PovsExpertiseRightSideContainer>
            <CustomSelect
              onChange={(value) => {
                const selectedValue = value as string;

                switch (value as string) {
                  case 'file_textbook':
                    setCurrentBook(expertise?.textbook?.file_textbook?.file_path || '');

                    return;
                  case 'file_instruction':
                    setCurrentBook(expertise?.textbook?.file_instruction?.file_path || '');

                    return;
                  case 'file_guideline':
                    setCurrentBook(expertise?.textbook?.file_guideline?.file_path || '');

                    return;

                  default:
                    if (selectedValue.startsWith('manual_file')) {
                      const index = parseInt(selectedValue.substring(11));
                      setCurrentBook(expertise?.textbook?.text_book_edu_man[index]?.file?.file_path || '');
                    }

                    return;
                }
              }}
              value={currentSelectValue || 'Файлы в данном учебнике отсутствуют'}
            >
              {expertise?.textbook?.file_textbook && (
                <Option value="file_textbook" key="file_textbook">
                  Учебник в электронном формате
                </Option>
              )}
              {expertise?.textbook?.file_instruction && (
                <Option value="file_instruction" key="file_instruction">
                  Интсрукция по установке, настройке и использованию электронной формы учебника
                </Option>
              )}
              {expertise?.textbook?.file_guideline && (
                <Option value="file_guideline" key="file_guideline">
                  Методическое пособие для учителя
                </Option>
              )}
              {expertise?.textbook?.text_book_edu_man?.map(
                (el, idx) =>
                  el.file?.file_path && (
                    <Option value={`manual_file${idx}`} key={`manual_file${idx}`}>
                      {`Учебное пособие ${el.title}`}
                    </Option>
                  ),
              )}
            </CustomSelect>
            <FormBlock>
              <GridItem>
                <CustomButton width="50px" onClick={() => setOpenBook(currentBook)} lined padding="5px 15px">
                  <ArrowsAltOutlined />
                </CustomButton>
              </GridItem>
              <DocumentPdf file={currentBook} />
            </FormBlock>
          </PovsExpertiseRightSideContainer>
        </div>
        <br />
        <GridItem direction="row" gap="35px" align="center" justify="center">
          <CustomButton
            disabled={!expertListInputState}
            disable={!expertListInputState}
            onClick={() => setOpen(true)}
            lined
          >
            Посмотреть
          </CustomButton>
          <ExpertModelQues
            bookAdaptive={bookAdaptive}
            bookCartographic={bookCartographic}
            open={open}
            setOpen={(value) => setOpen(value)}
            currentQuestion={expertListInputState}
          />
          <CustomButton onClick={updateExpertiseList}> Отправить</CustomButton>
        </GridItem>
      </FormContainer>
    </ExpertiseComponentContainer>
  );
};
